import { Component, Input, TemplateRef, OnInit, OnDestroy, Output, EventEmitter } from "@angular/core";
import { HttpService, AppData, NotifyService } from "@src/app/shared/services";
import { IUserAccount, Page } from "@shared/models";
import { ApiResources, UtilHelper, LinqHelper } from "@shared/helpers";
import { NgbModal, NgbModalRef } from "@ng-bootstrap/ng-bootstrap";
import { finalize, takeUntil } from "rxjs/operators";
import * as ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import { LabParameterInputModel, LabTemplateHeaderForInput, LabParameterInputHeaderModel, LabComponentHeaderForInput, LabParameterGroup, LabTransferModel } from "@admin/labs/pages/models";
import { forkJoin } from "rxjs";


class TemplateInput {
    newLabBookingDetailId: number;
    labTemplateObservedValueId: number;
    labTemplateHeaderId: number;
    isMethod: boolean;
    methodText: string;
    isInterpretation: boolean;
    interpretationText: string;
    labMainDetailId: number;
}

class TempParameter {
    labParameterObservedValueId?: number;
    labComponentHeaderId: number;
    componentName: string;
    labTemplateHeaderId: number;
    templateName: string;
    labParameterHeaderId: number;
    parameterName: string;
    displayName: string;
    referenceOutput: string;
    observedValue: string;
    templateCount: number;
    componentCount: number;
    labParameterDetailId?: number;
}

@Component({
    selector: "parameter-input",
    templateUrl: "./parameter-input.widget.html"
})
export class ParameterInputWidget implements OnInit, OnDestroy {
    @Input() newLabBookingDetailId: number;
    @Input() verifyAndSubmit: boolean = false;
    @Input() fromDoc: boolean = false;
    @Input() labSampleCollectionDetailId?: number = null;
    @Output() onClose = new EventEmitter<any>();
    page: Page;
    loading: boolean;
    modalRef: NgbModalRef;
    parameters: LabParameterInputModel;
    reports: LabParameterInputModel;

    notesEditor = ClassicEditor;
    config = {
        placeholder: "Enter your content here!",
        toolbar: ["heading", "|", "bold", "italic", "link", "bulletedList", "numberedList", "blockQuote"]
    };
    selectedParameter: LabParameterInputHeaderModel;
    submitting: boolean;
    verifying: boolean;
    templateInput: Array<TemplateInput>;
    parameterInput: Array<TempParameter>;
    demographData: Array<LabTransferModel>;

    constructor(
        private readonly appData: AppData,
        private readonly modalService: NgbModal,
        private readonly httpService: HttpService,
        private readonly notifyService: NotifyService
    ) {
        this.page = new Page();
        this.parameters = new LabParameterInputModel();
        this.reports = new LabParameterInputModel();
        this.demographData = new Array<LabTransferModel>();
    }

    onOpenModal(content: TemplateRef<any>, size: string) {
        this.modalRef = this.modalService.open(content, {
            backdrop: "static",
            keyboard: false,
            centered: false,
            windowClass: "custom-modal effect-scale",
            size: size
        });
    }

    onCloseModal() {
        if (this.modalRef) {
            this.modalRef.close();
            this.modalRef = undefined;
        }
    }

    ngOnInit() {
        this.appData.userAccount
            .pipe(takeUntil(this.page.unSubscribe))
            .subscribe((userAccount: IUserAccount) => {
                if (userAccount) {
                    this.page.userAccount = userAccount;
                    this.onFetchInputParameters();
                } else {
                    this.page.userAccount = undefined;
                }
            });
    }

    ngOnDestroy() {
        this.page.unsubscribeAll();
    }

    onClosePopup() {
        this.onClose.emit();
    }

    private onFetchInputParameters() {
        const request = {
            newLabBookingDetailId: this.newLabBookingDetailId,
            labSampleCollectionDetailId: this.labSampleCollectionDetailId,
            fromTech: this.fromDoc ? "tech" : ""
        };

        let requestApi = new Array<any>();
        let demographApi = this.httpService.post(ApiResources.getURI(ApiResources.labTransaction.base, ApiResources.labTransaction.fetchReceivedLabs), request);
        requestApi.push(demographApi);

        let inputApi = this.httpService.post(ApiResources.getURI(ApiResources.labTransaction.base, ApiResources.labTransaction.labParameterForInput), request);
        requestApi.push(inputApi);
        this.loading = true;

        forkJoin([...requestApi])
            .pipe(takeUntil(this.page.unSubscribe))
            .pipe(finalize(() => this.loading = false))
            .subscribe((response: Array<any>) => {
                if (response[0].length > 0) {
                    this.demographData = response[0] as Array<LabTransferModel>;
                }
                if (response[1]) {
                    this.parameters = response[1] as LabParameterInputModel;
                    this.reports = new LabParameterInputModel();
                    this.reports = UtilHelper.clone(this.parameters);
                    this.reports.templates = new Array<LabTemplateHeaderForInput>();
                    this.parameters.templates.forEach((template: LabTemplateHeaderForInput) => {
                        let parameterList = new Array<LabParameterInputHeaderModel>();
                        template.components.forEach((component: LabComponentHeaderForInput) => {
                            component.parameters.forEach((param: LabParameterInputHeaderModel) => {
                                let observedValue = +param.observedValue;
                                let range = param.selected;
                                if (range != null) {
                                    let exactRange = range;
                                    if (exactRange.minValue > observedValue) {
                                        param.showText = "L";
                                        param.isBold = true;
                                    }
                                    else if (observedValue > +exactRange.maxValue) {
                                        param.showText = "H";
                                        param.isBold = true;
                                    }
                                }
                                parameterList.push(param);
                            });
                        });
                        template.parameters.forEach((param: LabParameterInputHeaderModel) => {
                            let observedValue = +param.observedValue;
                            let range = param.selected;
                            if (range != null) {
                                let exactRange = range;
                                if (exactRange.minValue > observedValue) {
                                    param.showText = "L";
                                    param.isBold = true;
                                }
                                else if (observedValue > +exactRange.maxValue) {
                                    param.showText = "H";
                                    param.isBold = true;
                                }
                            }
                            parameterList.push(param);
                        });
                        let templateToAssign = UtilHelper.clone(template) as LabTemplateHeaderForInput;

                        templateToAssign.components = new Array<LabComponentHeaderForInput>();
                        templateToAssign.parameters = new Array<LabParameterInputHeaderModel>();
                        LinqHelper.sort(parameterList, "displayOrder");
                        templateToAssign.parameters = parameterList;
                        this.reports.templates.push(templateToAssign);
                    });
                }
            });
    }

    onOpenForParameterSelection(content: TemplateRef<any>, data: LabParameterInputHeaderModel) {
        this.selectedParameter = data;
        this.onOpenModal(content, "xl");
    }

    onSelectParameter(selectedValue: LabParameterGroup) {
        this.selectedParameter.labParameterDetailId = selectedValue.labParameterDetailId;
        this.selectedParameter.selected = selectedValue;
        this.onCloseModal();
    }

    onSubmit() {        
        const BreakError = {};
        const req2 = {
            newLabBookingDetailId: this.newLabBookingDetailId,
        };

        req2["patientId"] = this.demographData[0].patientId;
        req2["urlLink"] = window.location.href;
        req2["testName"] = this.demographData[0].testName;
        let sampleNum = 0;
        if (this.demographData[0].status != this.demographData[0].subStatus) {
            let sampleNumber = this.demographData[0] && UtilHelper.isEmpty(this.demographData[0].sampleName) ? this.demographData[0].sampleName.split(" ") : new Array<string>();
            if (sampleNumber.length > 0) {
                sampleNum = +sampleNumber[1];
            }
        }
        try {
            const templates = this.parameters.templates;
            this.parameterInput = new Array<TempParameter>();
            this.templateInput = new Array<TemplateInput>();
            templates.forEach((template) => {
                if (this.demographData[0].status === this.demographData[0].subStatus) {
                    if (template.isMethod) {
                        if (UtilHelper.isEmpty(template.methodText) && UtilHelper.isEmpty(template.methodText.trim())) {
                            // ignore
                        } else {
                            this.notifyService.warning(`Please enter method text for template ${template.templateName}`);
                            throw BreakError;
                        }
                    }
                    if (template.isInterpretation && !UtilHelper.isEmpty(template.interpretationText)) {
                        if (UtilHelper.isEmpty(template.interpretationText) && UtilHelper.isEmpty(template.interpretationText.trim())) {
                            // ignore
                        } else {
                            this.notifyService.warning(`Please enter interpretation text for template ${template.templateName}`);
                            throw BreakError;
                        }
                    }
                }
                let param = 0;
                template.components.forEach((component: LabComponentHeaderForInput) => {
                    component.parameters.forEach((parameter: LabParameterInputHeaderModel) => {
                        let model = new TempParameter();
                        model.labComponentHeaderId = component.labComponentHeaderId;
                        model.labTemplateHeaderId = template.labTemplateHeaderId;
                        model.templateName = template.templateName;
                        model.labParameterHeaderId = parameter.labParameterHeaderId;
                        model.parameterName = parameter.parameterName;
                        model.referenceOutput = parameter.referenceOutput;
                        param = param + 1;
                        if (this.demographData[0].status != this.demographData[0].subStatus && sampleNum > 0 && sampleNum >= param) {
                            if (!UtilHelper.isEmpty(parameter.observedValue)) {
                                this.notifyService.warning(`Please enter value of  ${template.templateName} <i class="mdi mdi-arrow-right"></i> ${component.componentName} <i class="mdi mdi-arrow-right"></i> <b>parameter</b> ${parameter.parameterName}`);
                                throw BreakError;
                            }
                            else if (!UtilHelper.isEmpty(parameter.observedValue.trim())) {
                                this.notifyService.warning(`Please enter value of  ${template.templateName} <i class="mdi mdi-arrow-right"></i> ${component.componentName} <i class="mdi mdi-arrow-right"></i> <b>parameter</b> ${parameter.parameterName}`);
                                throw BreakError;
                            }
                        }
                        else if (this.demographData[0].status === this.demographData[0].subStatus) {
                            if (!UtilHelper.isEmpty(parameter.observedValue)) {
                                this.notifyService.warning(`Please enter value of  ${template.templateName} <i class="mdi mdi-arrow-right"></i> ${component.componentName} <i class="mdi mdi-arrow-right"></i> <b>parameter</b> ${parameter.parameterName}`);
                                throw BreakError;
                            }
                            else if (!UtilHelper.isEmpty(parameter.observedValue.trim())) {
                                this.notifyService.warning(`Please enter value of  ${template.templateName} <i class="mdi mdi-arrow-right"></i> ${component.componentName} <i class="mdi mdi-arrow-right"></i> <b>parameter</b> ${parameter.parameterName}`);
                                throw BreakError;
                            }
                        }
                        model.observedValue = parameter.observedValue;
                        model.labParameterObservedValueId = parameter.labParameterObservedValueId;
                        model.labParameterDetailId = parameter.labParameterDetailId;
                        this.parameterInput.push(model);
                    });
                });
                param = 0;
                template.parameters.forEach((parameter: LabParameterInputHeaderModel) => {
                    let model = new TempParameter();
                    model.labComponentHeaderId = 0;
                    model.labTemplateHeaderId = template.labTemplateHeaderId;
                    model.templateName = template.templateName;
                    model.labParameterHeaderId = parameter.labParameterHeaderId;
                    model.parameterName = parameter.parameterName;
                    model.referenceOutput = parameter.referenceOutput;
                    param = param + 1;
                    if (this.demographData[0].status != this.demographData[0].subStatus && sampleNum > 0 && sampleNum >= param) {
                        if (!UtilHelper.isEmpty(parameter.observedValue)) {
                            this.notifyService.warning(`Please enter value of  ${template.templateName} <i class="mdi mdi-arrow-right"></i> Direct Parameter <i class="mdi mdi-arrow-right"></i> <b>parameter</b> ${parameter.parameterName}`);
                            throw BreakError;
                        }
                        else if (!UtilHelper.isEmpty(parameter.observedValue.trim())) {
                            this.notifyService.warning(`Please enter value of  ${template.templateName} <i class="mdi mdi-arrow-right"></i> Direct Parameter <i class="mdi mdi-arrow-right"></i> <b>parameter</b> ${parameter.parameterName}`);
                            throw BreakError;
                        }
                    }
                    else if (this.demographData[0].status === this.demographData[0].subStatus) {
                        if (!UtilHelper.isEmpty(parameter.observedValue)) {
                            this.notifyService.warning(`Please enter value of  ${template.templateName} <i class="mdi mdi-arrow-right"></i> Direct Parameter <i class="mdi mdi-arrow-right"></i> <b>parameter</b> ${parameter.parameterName}`);
                            throw BreakError;
                        }
                        else if (!UtilHelper.isEmpty(parameter.observedValue.trim())) {
                            this.notifyService.warning(`Please enter value of  ${template.templateName} <i class="mdi mdi-arrow-right"></i> Direct Parameter <i class="mdi mdi-arrow-right"></i> <b>parameter</b> ${parameter.parameterName}`);
                            throw BreakError;
                        }
                    }
                    model.observedValue = parameter.observedValue;
                    model.labParameterObservedValueId = parameter.labParameterObservedValueId;
                    model.labParameterDetailId = parameter.labParameterDetailId;
                    this.parameterInput.push(model);
                });

                const templateToPush = {
                    labTemplateObservedValueId: template.labTemplateObservedValueId || 0,
                    newLabBookingDetailId: this.newLabBookingDetailId,
                    labTemplateHeaderId: template.labTemplateHeaderId,
                    isMethod: template.isMethod,
                    methodText: template.methodText,
                    isInterpretation: template.isInterpretation,
                    interpretationText: template.interpretationText,
                    labMainDetailId: this.parameters.labMainDetailId
                };
                if (template.isInterpretation || template.isMethod) {
                    this.templateInput.push(templateToPush);
                }
            });
            this.submitting = true;
            let requestApi = new Array<any>();
            if (this.parameterInput.length > 0) {
                const parameterRequest = {
                    newLabBookingDetailId: this.newLabBookingDetailId,
                    parameters: this.parameterInput,
                    createdBy: this.page.userAccount.accountId,
                    labSampleCollectionDetailId: this.labSampleCollectionDetailId
                }
                let parameterApi = this.httpService.post(ApiResources.getURI(ApiResources.labTransaction.base, ApiResources.labTransaction.addLabParameterInput), parameterRequest);
                requestApi.push(parameterApi);
            }
            if (this.templateInput.length > 0) {
                const templateRequest = {
                    newLabBookingDetailId: this.newLabBookingDetailId,
                    templates: this.templateInput,
                    createdBy: this.page.userAccount.accountId,
                    labSampleCollectionDetailId: this.labSampleCollectionDetailId
                }
                let templateApi = this.httpService.post(ApiResources.getURI(ApiResources.labTransaction.base, ApiResources.labTransaction.addTemplateInput), templateRequest);
                requestApi.push(templateApi);
            }

            forkJoin([...requestApi])
                .pipe(takeUntil(this.page.unSubscribe))
                .pipe(finalize(() => this.submitting = false))
                .subscribe((response: Array<any>) => {
                    if (response[0] as number > 0 && response[1] as number > 0) {
                        this.notifyService.successToast("Labs Parameters and Templates added successfully.");
                    }
                    else if (response[0] as number > 0) {
                        this.notifyService.successToast("Labs Parameters modified successfully.");
                    }
                    else if (response[1] as number > 0) {
                        this.notifyService.successToast("Labs Templates modified successfully.");
                    }
                    if (!this.verifyAndSubmit) {
                        this.onClosePopup();
                    } else {
                        if (this.page.userAccount.roleId === 3) {
                            this.onDoctorVerified();
                        }
                    }

                }, () => {
                    this.notifyService.defaultErrorToast();
                });
        } catch (err) {
            if (err !== BreakError) throw err;
        }

    }

    onDoctorVerified() {
        this.verifying = true;
        const request = {
            newLabBookingDetailId: this.newLabBookingDetailId
        };
        if (this.page.userAccount.roleId == 3) {
            request["doctorId"] = this.page.userAccount.referenceId;
            request["accountId"] = this.page.userAccount.accountId;
        }
        request["urlLink"] = window.location.href;
        request["patientId"] = this.demographData[0].patientId;
        request["testName"] = this.demographData[0].testName;
        request["roleId"] = this.page.userAccount.roleId;
        request["createdByName"] = this.page.userAccount.fullName;
        request["requisitionNumber"] = this.demographData[0].requisitionNumber;
        request["paymentStatus"] = this.demographData[0].paymentStatus;
        request["paymentType"] = this.demographData[0].paymentType;

        this.httpService.post(ApiResources.getURI(ApiResources.labs.base, ApiResources.labs.doctorVerifyReport), request)
            .pipe(takeUntil(this.page.unSubscribe))
            .pipe(finalize(() => this.verifying = false))
            .subscribe((response: number) => {
                if (response > 0) {
                    this.notifyService.successToast(`Test Report Verified Successfully.`);
                    this.onClosePopup();
                }
                if (response < 0) {
                    this.notifyService.successToast(`error in Test Verification`);
                }

            }, () => {
                this.notifyService.warningToast("Unable to Test Verification.");
            });

    }

    changeTextColor(param: LabParameterInputHeaderModel) {
        let observedValue = +param.observedValue;
        let range = param.selected;
        if (range != null) {
            let exactRange = range;
            param.showText = null;
            param.isBold = false;
            if (exactRange.minValue > observedValue) {
                param.showText = "L";
                param.isBold = true;
            }
            else if (observedValue > +exactRange.maxValue) {
                param.showText = "H";
                param.isBold = true;
            }
        }
    }
}